import { useQuery } from "@tanstack/react-query";

export type FeatureDef = { key: string; name: string; description: string };

export type EntitlementPlan = {
  productId: string | null;
  productName: string | null;
  priceId: string | null;
  interval: string | null;
  amount: number | null;
  currency: string | null;
};

export type Entitlement = {
  active: boolean;
  source: "admin" | "manual" | "stripe" | "trial" | "none";
  allFeatures: boolean;
  features: string[];
  plan: EntitlementPlan | null;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  manualPlanUntil: string | null;
  /** true = Zugriff auf ALLE Länder (Admin, Trial, unbegrenzter Override). */
  allCountries: boolean;
  /** Freigeschaltete Länder als Uppercase-ISO (leer wenn allCountries). */
  countries: string[];
};

/** Ein buchbares Land (Task #522). */
export type BookableCountry = {
  iso: string;
  name: string;
};

/** Ein buchbares Länder-Add-on inkl. Preisen. */
export type CountryAddon = {
  id: string;
  country: string;
  name: string;
  description: string | null;
  prices: PackagePrice[];
};

export type TrialState = {
  available: boolean;
  active: boolean;
  used: boolean;
  startedAt: string | null;
  expiresAt: string | null;
};

/** Countdown bis zur automatischen Konto-Löschung (60-Tage-Frist ohne Abo). */
export type DeletionInfo = {
  /** Geplanter Löschzeitpunkt (ISO) oder null bei aktivem Zugang. */
  scheduledAt: string | null;
  /** Verbleibende Tage (0..60) oder null bei aktivem Zugang. */
  daysRemaining: number | null;
  /** Ist der Zugang aktiv (kein Countdown)? */
  active: boolean;
};

export type SubscriptionMe = {
  entitlement: Entitlement;
  trial: TrialState;
  trialDurationHours: number;
  featureCatalog: FeatureDef[];
  countryCatalog?: BookableCountry[];
  defaultBaseCountry?: string;
  deletion?: DeletionInfo;
};

export type PackagePrice = {
  id: string;
  unitAmount: number | null;
  currency: string;
  interval: string | null;
  intervalCount: number | null;
  nickname: string | null;
};

export type SubscriptionPackage = {
  id: string;
  name: string;
  description: string | null;
  order: number | null;
  features: string[];
  defaultPriceId: string | null;
  prices: PackagePrice[];
};

export type PackagesResponse = {
  packages: SubscriptionPackage[];
  countryAddons?: CountryAddon[];
  countryCatalog?: BookableCountry[];
  defaultBaseCountry?: string;
  featureCatalog: FeatureDef[];
};

/** Liest den serverseitig berechneten Abo-/Berechtigungsstatus. */
export function useSubscription() {
  return useQuery<SubscriptionMe>({ queryKey: ["/api/subscription/me"] });
}

/**
 * Prüft, ob ein Entitlement ein bestimmtes Feature freischaltet. Admin- und
 * Trial-Berechtigungen (allFeatures) schalten alles frei. Quelle der Wahrheit
 * bleibt die Server-Prüfung (requireFeature); diese Helfer-Funktion steuert nur
 * das UI (Anzeigen/Verbergen + Upgrade-Hinweis).
 */
export function entitlementHasFeature(
  entitlement: Entitlement | null | undefined,
  feature: string,
): boolean {
  if (!entitlement || !entitlement.active) return false;
  if (entitlement.allFeatures) return true;
  return entitlement.features.includes(feature);
}

/**
 * Prüft, ob ein Entitlement ein bestimmtes Land freischaltet (Task #522).
 * Quelle der Wahrheit bleibt die Server-Prüfung (countryGate); diese Funktion
 * steuert nur das UI (Ausgrauen gesperrter Länder + „Land freischalten").
 */
export function entitlementHasCountry(
  entitlement: Entitlement | null | undefined,
  iso: string | null | undefined,
): boolean {
  if (!entitlement || !entitlement.active) return false;
  if (entitlement.allCountries) return true;
  const norm = (iso ?? "").trim().toUpperCase();
  if (!norm) return false;
  return entitlement.countries.includes(norm);
}

/** React-Hook-Variante: liest den Abo-Status und prüft ein Feature. */
export function useHasFeature(feature: string): {
  hasFeature: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useSubscription();
  return {
    hasFeature: entitlementHasFeature(data?.entitlement, feature),
    isLoading,
  };
}

/**
 * Lädt den öffentlichen Paket-Katalog (ohne Login) für die Werbe-Ansicht auf
 * den Login-/Registrieren-Seiten. Liefert dieselben allgemeinen Katalog-Daten
 * wie die eingeloggte `/pakete`-Seite, aber ohne nutzerspezifische Felder.
 */
export function usePublicPackages() {
  return useQuery<PackagesResponse>({ queryKey: ["/api/public/packages"] });
}

/**
 * Wählt den Preis für ein Intervall (Monat/Jahr) und fällt sonst auf den
 * ersten verfügbaren Preis zurück. Gemeinsam genutzt von der Paketauswahl
 * (`Packages.tsx`) und der öffentlichen Werbe-Ansicht (`PackagesPromo.tsx`).
 */
export function priceForInterval(
  pkg: SubscriptionPackage,
  interval: "month" | "year",
): PackagePrice | null {
  return pkg.prices.find((p) => p.interval === interval) ?? pkg.prices[0] ?? null;
}

export function euro(cents: number | null, currency = "eur"): string {
  if (cents == null) return "—";
  try {
    return (cents / 100).toLocaleString("de-DE", {
      style: "currency",
      currency: currency.toUpperCase(),
    });
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export function intervalLabel(interval: string | null): string {
  if (interval === "month") return "monatlich";
  if (interval === "year") return "jährlich";
  if (interval === "week") return "wöchentlich";
  if (interval === "day") return "täglich";
  return interval ?? "einmalig";
}

export function intervalSuffix(interval: string | null): string {
  if (interval === "month") return "/ Monat";
  if (interval === "year") return "/ Jahr";
  return "";
}

export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}
